import PermissionChecker from 'src/modules/auth/permissionChecker';
import {isCustomRoleToken} from 'src/security/customRoleConstants';
import Roles from 'src/security/roles';
import { taskKanbanGestorFeatureGrantsKanbanAdminEquivalent } from 'src/security/taskKanbanGestorAccess';

/**
 * Permisos para los que, en UI, si el usuario tiene `customRole:…`, solo cuenta
 * `tenantAccessContext.customPermissionIds` (no roles fijos como ejecutor).
 * Admin sigue pudiendo por rol o por custom.
 */
const SCOPED_IDS = new Set<string>(['tareaCreate', 'firmaRead']);

function hasCustomRoleAssignment(checker: PermissionChecker): boolean {
  const ids = checker.currentUserRolesIds;
  if (!Array.isArray(ids) || !ids.length) {
    return false;
  }
  return ids.some((r) => isCustomRoleToken(r));
}

/** Si el permiso aplicaría ignorando el plan (roles fijos ∪ custom, o solo custom si aplica scope). */
function permissionGrantedIgnoringPlan(
  checker: PermissionChecker,
  permission: {id: string; allowedRoles: unknown},
): boolean {
  const scoped = SCOPED_IDS.has(permission.id);

  if (scoped && checker.rolesMatchOneOf([Roles.values.admin])) {
    return (
      checker.rolesMatchOneOf(permission.allowedRoles) ||
      checker.customPermissionIdsMatch(permission)
    );
  }

  if (scoped && hasCustomRoleAssignment(checker)) {
    return checker.customPermissionIdsMatch(permission);
  }

  if (
    taskKanbanGestorFeatureGrantsKanbanAdminEquivalent(
      checker.getResolvedTenantAccessContext()?.featureKeys,
      permission,
    )
  ) {
    return true;
  }

  if (checker.rolesMatchOneOf(permission.allowedRoles)) {
    return true;
  }
  return checker.customPermissionIdsMatch(permission);
}

/**
 * Igual que `PermissionChecker.match` salvo para ids en la lista interna con usuario customRole.
 */
export function matchPermissionForUi(
  checker: PermissionChecker,
  permission: {
    id: string;
    allowedPlans: unknown;
    allowedRoles: unknown;
  } | null,
): boolean {
  if (!permission) {
    return true;
  }
  if (!checker.planMatchOneOf(permission.allowedPlans)) {
    return false;
  }
  return permissionGrantedIgnoringPlan(checker, permission);
}

/**
 * Equivalente a `lockedForCurrentPlan` pero alineado con `matchPermissionForUi` en permisos acotados.
 */
export function lockedForCurrentPlanForUi(
  checker: PermissionChecker,
  permission: {
    id: string;
    allowedPlans: unknown;
    allowedRoles: unknown;
  } | null,
): boolean {
  if (!permission) {
    return false;
  }
  if (!permissionGrantedIgnoringPlan(checker, permission)) {
    return false;
  }
  return !checker.planMatchOneOf(permission.allowedPlans);
}
